"use client"

import { ChevronRight, Home } from "lucide-react"
import * as React from "react"
import { cn } from "../../lib/utils"

export interface BreadcrumbItem {
  label: string
  href?: string
  icon?: React.ReactNode
  current?: boolean
  onClick?: () => void
}

interface BreadcrumbProps extends React.HTMLAttributes<HTMLDivElement> {
  items: BreadcrumbItem[]
  separator?: React.ReactNode
  homeHref?: string
  showHomeIcon?: boolean
  onHomeClick?: () => void
}

const Breadcrumb = React.forwardRef<HTMLDivElement, BreadcrumbProps>(
  ({ className, items, separator = <ChevronRight className="h-4 w-4" />, homeHref = "/", showHomeIcon = true, onHomeClick, ...props }, ref) => {
    return (
      <nav
        ref={ref}
        className={cn("flex items-center text-sm text-muted-foreground", className)}
        aria-label="Breadcrumb"
        {...props}
      >
        <ol className="flex flex-wrap items-center space-x-1.5">
          {showHomeIcon && (
            <li className="inline-flex items-center">
              <a
                href={homeHref}
                className="inline-flex items-center text-muted-foreground hover:text-foreground"
                onClick={(e) => {
                  if (onHomeClick) {
                    e.preventDefault();
                    onHomeClick();
                  }
                }}
              >
                <Home className="h-4 w-4" />
              </a>
            </li>
          )}

          {items.map((item, index) => {
            const isLast = index === items.length - 1

            return (
              <li key={index} className="inline-flex items-center">
                {index > 0 || showHomeIcon ? (
                  <span className="mx-1.5 text-muted-foreground">{separator}</span>
                ) : null}

                {item.href || item.onClick ? (
                  <a
                    href={item.href || "#"}
                    className="inline-flex items-center hover:text-foreground"
                    onClick={(e) => {
                      if (item.onClick) {
                        e.preventDefault();
                        item.onClick();
                      }
                    }}
                  >
                    {item.icon && <span className="mr-1.5">{item.icon}</span>}
                    <span>{item.label}</span>
                  </a>
                ) : (
                  <span
                    className={cn(
                      "inline-flex items-center",
                      isLast ? "font-medium text-foreground" : "",
                      item.current ? "font-medium text-foreground" : ""
                    )}
                    aria-current={isLast || item.current ? "page" : undefined}
                  >
                    {item.icon && <span className="mr-1.5">{item.icon}</span>}
                    <span>{item.label}</span>
                  </span>
                )}
              </li>
            )
          })}
        </ol>
      </nav>
    )
  }
)

Breadcrumb.displayName = "Breadcrumb"

export { Breadcrumb }
